import { Provider, Signal, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { from } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import {
  PAPER_TRANSLATOR,
  PAPER_IS_PRODUCTION,
  PAPER_LOCALE,
  PAPER_CONTACT_SOURCE,
  PAPER_ACTION_CHECKER,
  PAPER_ANALYTICS,
  PaperDefaultTranslator,
  providePaperAccentStorage,
  providePaperTestIds,
  type PaperTranslator,
  type PaperContactSource,
  type PaperContactSearchResult,
  type PaperAnalyticsAdapter,
} from '@ricoh-paper/angular';
import { providePaperPanelWidthStorage } from '@ricoh-paper/angular/panels';
import {
  paperLocalThemeMemory,
  providePaperThemeAdapter,
  providePaperThemeMemory,
} from '@ricoh-paper/angular/shell';
import { environment } from '../../../environments/environment';
import { AnalyticsService } from './analytics.service';
import { appPanelWidthStorage } from './side-panel.service';
import { ThemeService } from './theme.service';
import { REMEMBERED_PAPERS_KEY } from '../../features/shell/components/workspace-nav/workspace-nav.model';
import { GraphQLClientService } from './graphql-client.service';
import { LocaleService } from './locale.service';
import { UserPreferencesService } from './user-preferences.service';
import { ViewerIdentityService } from './viewer-identity.service';
import { matchesActionHierarchical } from '../constants/action-catalogue';
import { ANALYTICS_EVENTS } from '../analytics/events';

/**
 * Employee-directory search backing `paper-contact-picker`'s "Pick from
 * team" typeahead. Identical to the query the picker ran in-component
 * before the library decoupling.
 */
const SEARCH_EMPLOYEES_QUERY = `
  query employees($search: String!) {
    employees(search: $search, take: 8) {
      _id firstName lastName name email picture jobTitle department
    }
  }
`;

/**
 * Bridges between the app and the Ricoh Paper design-system library.
 *
 * The library is app-agnostic: components inject `PAPER_TRANSLATOR`,
 * `PAPER_LOCALE`, `PAPER_IS_PRODUCTION`, `PAPER_CONTACT_SOURCE`,
 * `PAPER_ACTION_CHECKER`, `PAPER_ANALYTICS`, `PAPER_ACCENT_STORAGE` and
 * `PAPER_PANEL_WIDTH_STORAGE` instead
 * of Transloco / LocaleService / `environments/*` / GraphQL /
 * ViewerIdentityService / AnalyticsService / UserPreferencesService.
 * These root providers back those tokens with the app's real services so
 * runtime language switching, locale detection, prod gating, the
 * contact-picker's directory search, `*paperHasAction` permission
 * gating, and design-system interaction diagnostics keep flowing
 * through the existing app machinery. Without these providers the
 * library falls back to its shipped en/ja dictionaries + browser locale
 * + production defaults + an empty contact directory + an
 * allow-everything action checker + a silent no-op analytics adapter.
 *
 * Spread into `appConfig.providers` after `provideAppTransloco()`.
 */
export function providePaperBridges(): Provider[] {
  return [
    {
      provide: PAPER_TRANSLATOR,
      useFactory: (): PaperTranslator => {
        const transloco = inject(TranslocoService);
        const lang = toSignal(transloco.langChanges$, {
          initialValue: transloco.getActiveLang(),
        }) as Signal<string>;
        // The library ships en/ja for every `shared.paper_*` key. The app
        // overrides only the subset it has localised in Transloco; for any DS
        // key Transloco doesn't define (it returns the key unchanged on a
        // miss) fall back to the library's shipped strings, so chart /
        // admin-rail / etc. labels never render as raw keys.
        const fallback = new PaperDefaultTranslator();
        // Keep the fallback translator's language in sync OUTSIDE the render
        // path. `setLang()` writes a PaperDefaultTranslator signal; calling it
        // inside `translate()` (which the `paperT` pipe runs during change
        // detection) is a signal write mid-render → NG0600, flooding on every
        // render. An effect runs after render, so the write is legal and fires
        // only when the active language actually changes.
        effect(() => fallback.setLang(lang()));
        return {
          lang,
          // Only ask Transloco for keys the APP actually defines; otherwise go
          // straight to the library's shipped string. `transloco.translate()` on
          // a key the app doesn't override trips Transloco's missing-key handler,
          // which logs `Missing translation for '<key>'` on EVERY render — a
          // flood for the many DS-only keys (e.g. `shared.paper_rich_text.*`) the
          // app never localises. Membership-check the active dictionary first so
          // a miss falls back silently (the render is already correct via the
          // fallback translator's en/ja strings).
          translate: (key, params) => {
            const dict = transloco.getTranslation(transloco.getActiveLang());
            if (dict && key in dict) {
              const value = transloco.translate(
                key,
                params as Record<string, unknown> | undefined,
              );
              if (value != null && value !== key && value !== '') return value;
            }
            return fallback.translate(key, params);
          },
        };
      },
    },
    {
      provide: PAPER_LOCALE,
      useFactory: () => inject(LocaleService).locale,
    },
    {
      provide: PAPER_IS_PRODUCTION,
      useValue: environment.production,
    },
    {
      provide: PAPER_ACTION_CHECKER,
      useFactory: (): Signal<(action: string) => boolean> => {
        const identity = inject(ViewerIdentityService);
        // Mirrors the app's `*imHasAction` directive exactly: the viewer's
        // RAW action set + hierarchical wildcard matching, no location or
        // division scoping (UI gating only — the server enforces scope).
        // Reactive: the predicate is recreated whenever the viewer's
        // action set changes, so `*paperHasAction` re-evaluates.
        return computed(() => {
          const actions = identity.actions();
          return (action: string) => matchesActionHierarchical(actions, action);
        });
      },
    },
    {
      provide: PAPER_ANALYTICS,
      useFactory: (): PaperAnalyticsAdapter => {
        const analytics = inject(AnalyticsService);
        // The suppress / map / forward bridge (ADR-003):
        //   SUPPRESS paper-button 'clicked' — host surfaces own their
        //     funnel events (ADR-001's authority rule); forwarding the
        //     library's generic click would double-count every CTA.
        //   MAP the raw parts into the paper.<component>.<action>
        //     namespace (selector prefix stripped: 'paper-page-tabs' →
        //     'page-tabs') and wrap in the single catalogued passthrough
        //     event — AnalyticsService.capture is strictly
        //     catalogue-typed, and PAPER_COMPONENT_INTERACTION keeps that
        //     type safety intact instead of loosening capture to accept
        //     arbitrary event names.
        //   FORWARD the library's component-state metadata untouched
        //     (ids/enums/counts only — the lib enforces no-PII payloads).
        // Opt-out needs no handling here: AnalyticsService delegates to
        // PostHog, which already swallows captures for opted-out users.
        return {
          trackComponentInteraction: (componentName, action, props) => {
            if (componentName === 'paper-button') return;
            const component = componentName.replace(/^paper-/, '');
            analytics.capture(ANALYTICS_EVENTS.PAPER_COMPONENT_INTERACTION, {
              ...props, // metadata first — the reserved keys below always win
              component,
              action,
              interaction: `paper.${component}.${action}`,
            });
          },
        };
      },
    },
    {
      provide: PAPER_CONTACT_SOURCE,
      useFactory: (): PaperContactSource => {
        const graphql = inject(GraphQLClientService);
        return {
          search: (term: string) =>
            from(
              (async (): Promise<PaperContactSearchResult[]> => {
                if (!term) return [];
                try {
                  const result = await graphql.query<PaperContactSearchResult[]>(
                    SEARCH_EMPLOYEES_QUERY,
                    { search: term },
                  );
                  return Array.isArray(result) ? result : [];
                } catch {
                  return [];
                }
              })(),
            ),
        };
      },
    },
    // The accent's store (PaperAccentService, behind AccentService): the
    // person's choice lives in UserPreferences.brandColorOverride, which
    // ProfileSettingsSyncService syncs to the User document. `read` is the
    // preference signal, so a hydrate that folds the server's choice in is
    // followed at once.
    providePaperAccentStorage(() => {
      const prefs = inject(UserPreferencesService);
      return {
        read: () => prefs.brandColorOverride(),
        write: (value: string) => prefs.update({ brandColorOverride: value }, { origin: 'user' }),
      };
    }),
    // The shared side-panel width (`SidePanelService` reads it through
    // `PaperPanelWidthService`), kept under the app's own localStorage key.
    providePaperPanelWidthStorage(appPanelWidthStorage),
    // Paper's theme controls (the profile panel's Light · Graphite · Black
    // switch and its All themes list) wear and write the app's ThemeService,
    // which persists and syncs the choice as every other theme control does.
    // A change made there is the panel's appearance change, so the event the
    // panel's own switch fired moves here with it.
    providePaperThemeAdapter(() => {
      const theme = inject(ThemeService);
      const analytics = inject(AnalyticsService);
      return {
        theme: theme.theme,
        setTheme: (name) => {
          const from = theme.theme();
          if (name === from) return;
          theme.setTheme(name);
          analytics.capture(ANALYTICS_EVENTS.APPEARANCE_THEME_CHANGED, { from, to: theme.theme() });
        },
      };
    }),
    // The last light and dark papers, under the key the workspace nav's theme
    // chip already keeps them in, so the two controls agree.
    providePaperThemeMemory(() => paperLocalThemeMemory(REMEMBERED_PAPERS_KEY)),
    // Paper 0.48 forwards a static `data-testid` to the native control and, by
    // default, keeps it on the host too. Pinned on purpose: 111 references in
    // 36 of this app's specs scope through the host (`paper-input[data-testid]
    // input` and the like; Paper's docs/testing.md measured them), and they
    // would break if the host copy went (ruling 17: never drop an id a spec
    // uses). Turn it off once those specs select the native control.
    providePaperTestIds({ keepOnHost: true }),
  ];
}
